import jsPDF from 'jspdf';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import {
  MultiNavLeft,
  MultiNavRight,
  SingleNavLeft,
  SingleNavRight,
  ZoomIn,
  ZoomOut,
} from '../../../assets/images/index.js';
import styles from './PdfPreview.module.scss';
 
const PDFViewer = ({ base64Pdf, fileName, fileType }) => {
  const isImageFile = fileType?.startsWith('image/');
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(0.9);
  const [customPage, setCustomPage] = useState('1');
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null); // To keep track of the current render task
 
  GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;
   
  const getImageDimensions = (base64ImageData, fileType) => {
    const binaryString = atob(base64ImageData);
    const data = new Uint8Array(Array.from(binaryString, (char) => char.charCodeAt(0)));
 
    if (fileType === 'image/jpeg' || fileType === 'image/jpg') {
      let i = 0;
      while (i < data.length) {
        if (data[i] === 0xff && data[i + 1] === 0xc0) {
          const height = (data[i + 5] << 8) + data[i + 6];
          const width = (data[i + 7] << 8) + data[i + 8];
          return { width, height };
        }
        i++;
      }
      throw new Error('Invalid JPEG data: Dimensions not found');
    } else if (fileType === 'image/png') {
      if (data[0] === 0x89 && data[1] === 0x50 && data[2] === 0x4e && data[3] === 0x47) {
        const width = (data[16] << 24) + (data[17] << 16) + (data[18] << 8) + data[19];
        const height = (data[20] << 24) + (data[21] << 16) + (data[22] << 8) + data[23];
        return { width, height };
      }
      throw new Error('Invalid PNG data: IHDR chunk not found');
    } else {
      throw new Error(`Unsupported file type: ${fileType}`);
    }
  };
 
  const convertImageToBase64Pdf = async (base64ImageData, fileType) => {
    const { width, height } = getImageDimensions(base64ImageData, fileType);
 
    const MAX_WIDTH = 1000;
    const MAX_HEIGHT = 1000;
    let scaledWidth = width;
    let scaledHeight = height;
    const aspectRatio = width / height;
 
    // Scale down if needed
    if (width > MAX_WIDTH || height > MAX_HEIGHT) {
      if (aspectRatio > 1) {
        scaledWidth = MAX_WIDTH;
        scaledHeight = MAX_WIDTH / aspectRatio;
      } else {
        scaledHeight = MAX_HEIGHT;
        scaledWidth = MAX_HEIGHT * aspectRatio;
      }
    }
 
    const pdf = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'pt',
      format: [scaledWidth, scaledHeight], // Dynamically set page size
    });
 
    pdf.addImage(
      `data:${fileType};base64,${base64ImageData}`,
      fileType === 'image/png' ? 'PNG' : 'JPEG',
      0,
      0,
      scaledWidth,
      scaledHeight
    );
 
    const base64Pdf = pdf.output('datauristring');
    const prefixToRemove = 'data:application/pdf;filename=generated.pdf;base64,';
    return base64Pdf.replace(prefixToRemove, '');
  };
 
  useEffect(() => {
    const isPdfFile = fileType === 'application/pdf';
    if (isImageFile) {
      const fetchPDF = async () => {
        const pdfData = await convertImageToBase64Pdf(base64Pdf, fileType);
        const loadedPdf = await getDocument({ data: atob(pdfData) }).promise;
        setPdf(loadedPdf);
        setTotalPages(loadedPdf.numPages);
      };
 
      if (base64Pdf) {
        fetchPDF();
      }
    } else if (isPdfFile) {
      const fetchPDF = async () => {
        const loadedPdf = await getDocument({ data: atob(base64Pdf) }).promise;
        setPdf(loadedPdf);
        setTotalPages(loadedPdf.numPages);
      };
 
      if (base64Pdf) {
        fetchPDF();
      }
    } else {
      console.error('Unsupported file type:', fileType);
    }
  }, [base64Pdf, fileType]);
 
  const renderPage = async (pageNum, currentScale) => {
    if (!pdf || !canvasRef.current) {return;}
 
    const page = await pdf.getPage(pageNum);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const viewport = page.getViewport({ scale: currentScale });
 
    canvas.width = viewport.width;
    canvas.height = viewport.height;
 
    // Cancel any ongoing render task before starting a new one
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }
 
    // Start a new render task
    renderTaskRef.current = page.render({ canvasContext: context, viewport });
    try {
      await renderTaskRef.current.promise;
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Render error:', error);
      }
    }
  };
 
  useEffect(() => {
    if (pdf) {
      renderPage(currentPage, scale);
    }
  }, [currentPage, pdf, scale]);
 
  const handlePageInput = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= totalPages))) {
      setCustomPage(value);
    }
  };
 
  const handlePageInputSubmit = (e) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(customPage, 10);
      if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
        setCurrentPage(pageNum);
      } else {
        setCustomPage(currentPage.toString());
      }
    }
  };
 
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => {
        const nextPage = prevPage + 1;
        setCustomPage(nextPage.toString());
        return nextPage;
      });
    }
  };
 
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => {
        const prevPageNum = prevPage - 1;
        setCustomPage(prevPageNum.toString());
        return prevPageNum;
      });
    }
  };
 
  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.1));
  };
 
  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.9));
  };
 
  return (
    <div className={styles.pdfContainer}>
      <div className={styles.pdfToolbar}>
        <div className={styles.toolbarLeft}>
          <span className={styles.fileName}>{fileName || 'Document'}</span>
        </div>
        <div className={styles.toolbarCenter}>
          <button onClick={handleZoomIn} title="Zoom In">
            <img src={ZoomIn} alt="Zoom In" style={{ width: '24px', height: '24px' }} />
          </button>
          <button onClick={handleZoomOut} title="Zoom Out">
            <img src={ZoomOut} alt="Zoom Out" style={{ width: '24px', height: '24px' }} />
          </button>
        </div>
      </div>
      <div className={styles.pdfContent}>
        <canvas ref={canvasRef} />
      </div>
      {!isImageFile && <div className={styles.pdfPagination}>
        <button
          onClick={() => {
            setCurrentPage(1);
            setCustomPage('1');
          }}
          disabled={currentPage === 1}
          title="First Page"
          data-testid="pdf-first-page-button"
        >
          <img src={SingleNavLeft} />
        </button>
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          title="Previous Page"
          data-testid="pdf-prev-page-button"
        >
          <img src={MultiNavLeft} />
        </button>
        <span>Page</span>
        <input
          type="text"
          className={styles.pageInput}
          value={customPage}
          onChange={handlePageInput}
          onKeyDown={handlePageInputSubmit}
          title="Enter page number"
          data-testid="pdf-page-input"
        />
        <span>of {totalPages}</span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          title="Next Page"
          data-testid="pdf-next-page-button"
        >
          <img src={SingleNavRight} />
        </button>
        <button
          onClick={() => {
            setCurrentPage(totalPages);
            setCustomPage(totalPages.toString());
          }}
          disabled={currentPage === totalPages}
          title="Last Page"
          data-testid="pdf-last-page-button"
        >
          <img src={MultiNavRight} />
        </button>
      </div>}
    </div>
  );
};
 
export default PDFViewer;
PDFViewer.propTypes = {
  base64Pdf: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  fileType: PropTypes.string.isRequired,
};


